import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  leftIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leftIcon, className, id, ...rest },
  ref,
) {
  // useId generates a stable ID that never changes between renders.
  // The previous Math.random() here was the root cause of the mobile
  // keyboard disappearing: a new random id on every keystroke made iOS
  // treat the focused input as a different element and dismiss the keyboard.
  const generatedId = useId();
  const inputId = id ?? `inp-${rest.name ?? generatedId}`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="text-[11px] font-semibold uppercase tracking-wider text-muted"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            "w-full h-10 px-3 rounded-md bg-surface2 border border-border2",
            "text-text placeholder:text-dim",
            "focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold-glow",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            leftIcon && "pl-9",
            error && "border-danger focus:border-danger focus:ring-[#552323]/40",
            className,
          )}
          {...rest}
        />
      </div>
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    label?: ReactNode;
    hint?: ReactNode;
    error?: ReactNode;
  }
>(function Textarea({ label, hint, error, className, id, ...rest }, ref) {
  const generatedId = useId();
  const inputId = id ?? `ta-${rest.name ?? generatedId}`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="text-[11px] font-semibold uppercase tracking-wider text-muted"
        >
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        className={cn(
          "w-full px-3 py-2 rounded-md bg-surface2 border border-border2",
          "text-text placeholder:text-dim",
          "focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold-glow",
          "disabled:opacity-50",
          "min-h-[88px] resize-y",
          error && "border-danger focus:border-danger",
          className,
        )}
        {...rest}
      />
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
});
